/**
 * Strongly typed showcase item returned from the Showcase API.
 * Each item represents one sub directory of the showcase git repository;
 * the metadata comes from the YAML frontmatter of its README.md.
 * @see https://smartdataproxy.azurewebsites.net/api/Showcase
 */
export interface ShowcaseItem {
    slug: string;
    title: string;
    description: string;
    author: string;
    date: Date | null;
    tags: string[];
    imageUrl: string;
    folderUrl: string;
}

/**
 * Raw item shape from the Showcase API (date is an ISO string, tags an array of keywords).
 */
interface ShowcaseRawItem {
    slug: string;
    title: string | null;
    description: string | null;
    author: string | null;
    date: string | null;
    tags: string[] | string | null;
    imageUrl: string | null;
    folderUrl: string | null;
}

const SHOWCASE_API_URL =
    process.env.REACT_APP_SHOWCASE_API_URL || 'https://smartdataproxy.azurewebsites.net/api/Showcase';

/**
 * Parses the frontmatter date. Date-only values (YYYY-MM-DD) are read as local dates,
 * otherwise new Date() would treat them as UTC and shift the day in western time zones.
 */
function parseDate(value: string | null): Date | null {
    if (!value) return null;
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    const date = dateOnly
        ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
        : new Date(value);
    return isNaN(date.getTime()) ? null : date;
}

/**
 * Normalizes the tags: trims entries, drops empty ones and duplicates (case-insensitive).
 * Also accepts a semicolon-separated string, the format used in the README frontmatter.
 */
function parseTags(value: string[] | string | null): string[] {
    const entries = Array.isArray(value) ? value : typeof value === 'string' ? value.split(';') : [];
    const seen = new Set<string>();
    const tags: string[] = [];
    entries.forEach((entry) => {
        const tag = typeof entry === 'string' ? entry.trim() : '';
        const key = tag.toLowerCase();
        if (tag && !seen.has(key)) {
            seen.add(key);
            tags.push(tag);
        }
    });
    return tags;
}

function mapRawToShowcaseItem(raw: ShowcaseRawItem): ShowcaseItem {

    return {
        slug: raw.slug,
        title: raw.title?.trim() || raw.slug,
        description: raw.description?.trim() ?? '',
        author: raw.author?.trim() ?? '',
        date: parseDate(raw.date),
        tags: parseTags(raw.tags),
        imageUrl: raw.imageUrl?.trim() ?? '',
        folderUrl: raw.folderUrl?.trim() ?? '',
    };
}

/**
 * Singleton provider for the Showcase API.
 * Caches the result; the REST endpoint is only queried when the cache is empty.
 */
export class ShowcaseProvider {
    private static instance: ShowcaseProvider;

    /** Cached showcase list; null = not yet fetched, array = cached (may be empty). */
    private static items: ShowcaseItem[] | null = null;

    private constructor() {}

    static getInstance(): ShowcaseProvider {
        if (!ShowcaseProvider.instance) {
            ShowcaseProvider.instance = new ShowcaseProvider();
        }
        return ShowcaseProvider.instance;
    }

    /**
     * Gets all showcases from the Showcase API.
     * Queries the REST endpoint only if the cache is empty;
     * otherwise returns the saved items.
     */
    async GetShowcases(): Promise<ShowcaseItem[]> {
        if (ShowcaseProvider.items !== null) {
            return ShowcaseProvider.items;
        }

        const response = await fetch(SHOWCASE_API_URL);
        if (!response.ok) {
            throw new Error(`Showcase API error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        const rawItems = Array.isArray(data) ? (data as ShowcaseRawItem[]) : [];
        const items = rawItems.filter((raw) => !!raw?.slug).map(mapRawToShowcaseItem);
        ShowcaseProvider.items = items;
        return items;
    }
}
